/**
 * Foundation Layer 2 — Auth (web).
 *
 * The single source of truth for the current user, session, access
 * token, and auth-state transitions. Raw `supabase.auth.*` calls
 * outside this module are forbidden (enforced by a structural
 * guardrail — 585 sites across 379 files at the time this layer
 * landed; drop the baseline in the same PR as each migration).
 *
 * Why a gateway:
 *   - Every feature asked Supabase directly, often duplicating the
 *     same `const { data: { user } } = await supabase.auth.getUser()`
 *     incantation. That's 585 chances to diverge on error handling,
 *     cache freshness, and token refresh semantics.
 *   - A gateway is the GCP→AWS migration seam for auth. When we
 *     swap providers, only this file changes.
 *   - A gateway is the only place auth state is observable. Features
 *     subscribe here instead of owning their own session cache.
 *
 * See `docs/LINKBOX_ARCHITECTURE.md` §2 for the layer contract.
 */

import type { Session, SupabaseClient, User, Subscription } from '@supabase/supabase-js'
import { createClient as createBrowserClient } from '@/lib/supabase/client'

// ─── Public types ────────────────────────────────────────────────────

export type AuthStateEvent =
    | { kind: 'signed_in'; user: User; session: Session }
    | { kind: 'signed_out' }
    | { kind: 'token_refreshed'; session: Session }
    | { kind: 'user_updated'; user: User }

export interface AuthSnapshot {
    user: User | null
    session: Session | null
    isAuthenticated: boolean
}

export class UnauthenticatedError extends Error {
    constructor(public readonly source?: string) {
        super('Not authenticated')
        this.name = 'UnauthenticatedError'
    }
}

export interface AuthGatewayOptions {
    /** Supabase client to wrap. Defaults to the browser client. */
    client?: SupabaseClient
    /**
     * Cache TTL for `currentUser()` / `currentSession()` reads. Prevents
     * every feature from hammering `supabase.auth.getUser()`. Default 3s.
     */
    readCacheTtlMs?: number
}

// ─── Gateway ─────────────────────────────────────────────────────────

export class AuthGateway {
    private readonly client: SupabaseClient
    private readonly readCacheTtlMs: number
    private cachedSnapshot: AuthSnapshot | null = null
    private cachedAt = 0
    private listeners = new Set<(event: AuthStateEvent) => void>()
    private supabaseSubscription: Subscription | null = null

    constructor(options: AuthGatewayOptions = {}) {
        this.client = options.client ?? createBrowserClient()
        this.readCacheTtlMs = options.readCacheTtlMs ?? 3_000
    }

    // ── Reads ────────────────────────────────────────────────────────

    /** The current user, or `null` if signed out. Cached for `readCacheTtlMs`. */
    async currentUser(): Promise<User | null> {
        return (await this.currentSnapshot()).user
    }

    /** The current session, or `null` if signed out. */
    async currentSession(): Promise<Session | null> {
        return (await this.currentSnapshot()).session
    }

    /** `true` if a user is signed in. */
    async isAuthenticated(): Promise<boolean> {
        return (await this.currentSnapshot()).isAuthenticated
    }

    /** The current access token, or `null` if signed out. */
    async accessToken(): Promise<string | null> {
        const s = await this.currentSession()
        return s?.access_token ?? null
    }

    /**
     * Returns the user or throws `UnauthenticatedError`. Use this at
     * the entry to any feature path that should never run unauthed.
     */
    async requireUser(source?: string): Promise<User> {
        const user = await this.currentUser()
        if (!user) throw new UnauthenticatedError(source)
        return user
    }

    /** Combined snapshot read — batches the Supabase call. */
    async currentSnapshot(): Promise<AuthSnapshot> {
        const fresh = Date.now() - this.cachedAt < this.readCacheTtlMs
        if (fresh && this.cachedSnapshot) return this.cachedSnapshot

        const [{ data: { user } }, { data: { session } }] = await Promise.all([
            this.client.auth.getUser(),
            this.client.auth.getSession(),
        ])
        this.cachedSnapshot = {
            user: user ?? null,
            session: session ?? null,
            isAuthenticated: !!user,
        }
        this.cachedAt = Date.now()
        return this.cachedSnapshot
    }

    /** Invalidates the read cache so the next call hits Supabase. */
    refresh(): void {
        this.cachedSnapshot = null
        this.cachedAt = 0
    }

    // ── State transitions (sign-in, sign-out) ────────────────────────

    async signOut(): Promise<void> {
        await this.client.auth.signOut()
        this.refresh()
    }

    // ── Observability ────────────────────────────────────────────────

    /**
     * Subscribe to auth-state events (sign-in, sign-out, token refresh,
     * user-updated). Lazily wires the underlying Supabase listener.
     * Returns an unsubscribe function.
     */
    subscribe(listener: (event: AuthStateEvent) => void): () => void {
        this.listeners.add(listener)
        this.ensureSupabaseSubscription()
        return () => {
            this.listeners.delete(listener)
            if (this.listeners.size === 0) this.teardownSupabaseSubscription()
        }
    }

    private ensureSupabaseSubscription(): void {
        if (this.supabaseSubscription) return
        const { data } = this.client.auth.onAuthStateChange((event, session) => {
            // Supabase emits strings like "SIGNED_IN" / "TOKEN_REFRESHED"
            // — normalise to our shape, invalidate cache, fan out.
            this.refresh()
            switch (event) {
                case 'SIGNED_IN':
                case 'INITIAL_SESSION':
                    if (session?.user) this.emit({ kind: 'signed_in', user: session.user, session })
                    break
                case 'SIGNED_OUT':
                    this.emit({ kind: 'signed_out' })
                    break
                case 'TOKEN_REFRESHED':
                    if (session) this.emit({ kind: 'token_refreshed', session })
                    break
                case 'USER_UPDATED':
                    if (session?.user) this.emit({ kind: 'user_updated', user: session.user })
                    break
            }
        })
        this.supabaseSubscription = data.subscription
    }

    private teardownSupabaseSubscription(): void {
        this.supabaseSubscription?.unsubscribe()
        this.supabaseSubscription = null
    }

    private emit(event: AuthStateEvent): void {
        for (const listener of this.listeners) {
            try {
                listener(event)
            } catch {
                // Listener errors must not cascade into other listeners.
            }
        }
    }
}

// ─── Singleton accessor ──────────────────────────────────────────────

let sharedGateway: AuthGateway | null = null

/** The one AuthGateway instance for the browser runtime. */
export function getAuthGateway(): AuthGateway {
    if (!sharedGateway) sharedGateway = new AuthGateway()
    return sharedGateway
}

/** Test-only: swap the shared instance. Restore with `resetAuthGatewayForTesting`. */
export function setAuthGatewayForTesting(gateway: AuthGateway): void {
    sharedGateway = gateway
}

export function resetAuthGatewayForTesting(): void {
    sharedGateway = null
}
